import type { OpusDecoderWebWorker } from "opus-decoder";
import {
  AudioDecoderError,
  SDKError,
  createUnknownError,
  withErrorData,
} from "../types/error.types";
import type {
  StreamData,
  AudioData,
  StreamDataHeader,
  StreamFormat,
} from "../types/model.types";
import { isAudioStreamFormat } from "../utils/type-helpers";
import { ThroughputTracker } from "../utils/throughput-tracker";

// Dynamic import to avoid SSR issues - Worker is not available in Node.js
let OpusDecoderWebWorkerClass: typeof OpusDecoderWebWorker | null = null;
async function getOpusDecoderWebWorker(): Promise<typeof OpusDecoderWebWorker> {
  if (!OpusDecoderWebWorkerClass) {
    const module = await import("opus-decoder");
    OpusDecoderWebWorkerClass = module.OpusDecoderWebWorker;
  }
  return OpusDecoderWebWorkerClass;
}
export interface LiveAudioDecoderOptions {
  format: StreamFormat;
  onStreamData: (streamData: StreamData) => void;
  onError: (error: SDKError) => void;
  packetLossSimulation?: {
    enabled: boolean;
    lossRate: number;
    burstLength: number;
  };
}

/**
 * LiveAudioDecoder class optimized for live audio streaming with packet loss resilience
 */
export class LiveAudioDecoder {
  private format: StreamFormat;
  private onStreamData: (streamData: StreamData) => void;
  private onError: (error: SDKError) => void;

  private textDecoder = new TextDecoder();
  private opusDecoder: OpusDecoderWebWorker<48000> | null = null;
  private isDecoderInitialized: boolean = false;

  // Queue management for live streaming
  private opusQueue: Array<{
    data: ArrayBuffer;
    receiveTime?: number;
    uuid?: string;
  }> = [];
  private isDecoding: boolean = false;
  private decodeCounter: number = 0;

  // Packet loss simulation state
  private packetLossConfig?: LiveAudioDecoderOptions["packetLossSimulation"];
  private consecutiveLossCount: number = 0;
  private totalPacketCount: number = 0;
  private throughputTracker: ThroughputTracker = new ThroughputTracker("live-decode");

  constructor(options: LiveAudioDecoderOptions) {
    this.format = options.format;
    this.onStreamData = options.onStreamData;
    this.onError = options.onError;
    this.packetLossConfig = options.packetLossSimulation;
  }

  private async initializeDecoder(): Promise<void> {
    console.debug("LiveAudioDecoder.initializeDecoder");
    if (this.isDecoderInitialized) return;

    try {
      // Dynamically import to avoid SSR issues
      const DecoderClass = await getOpusDecoderWebWorker();
      this.opusDecoder = new DecoderClass({
        sampleRate: 48000,
        channels: 2,
        forceStereo: true,
        preSkip: 0,
      }) as OpusDecoderWebWorker<48000>;

      await this.opusDecoder.ready;
      this.isDecoderInitialized = true;
      console.debug("LiveAudioDecoder: Web Worker decoder initialized");
    } catch (error) {
      const sdkError = withErrorData(
        AudioDecoderError.OpusDecoderInitializationFailed,
        {
          details: error instanceof Error ? error.message : String(error),
        },
      );
      this.onError(sdkError);
      throw sdkError;
    }
  }

  async decode(data: ArrayBuffer, receiveTime?: number, uuid?: string) {
    this.opusQueue.push({ data, receiveTime, uuid });
    if (!this.isDecoding) {
      this.processQueue();
    }
  }

  async reset() {
    this.opusQueue = [];
    this.decodeCounter++; // Increment to invalidate ongoing decodings

    if (this.opusDecoder) {
      try {
        await this.opusDecoder.reset();
      } catch (error) {
        console.warn("Failed to reset OpusDecoder:", error);
        // Create a new decoder instance if reset fails
        this.isDecoderInitialized = false;
        this.opusDecoder = null;
        await this.initializeDecoder();
      }
    }
  }

  /**
   * Destroy the decoder and release all resources.
   * Must be called when the decoder is no longer needed.
   */
  async destroy() {
    this.opusQueue = [];
    this.decodeCounter++;

    if (this.opusDecoder) {
      try {
        await this.opusDecoder.free();
        console.debug("LiveAudioDecoder: Worker terminated, WASM memory released");
      } catch (error) {
        console.warn("Failed to free OpusDecoder:", error);
      }
      this.opusDecoder = null;
    }

    this.isDecoderInitialized = false;
  }

  private async processQueue() {
    const currentDecodeCounter = this.decodeCounter;
    this.isDecoding = true;

    while (this.opusQueue.length > 0) {
      const queueItem = this.opusQueue.shift();
      if (!queueItem) {
        continue;
      }

      const { data, receiveTime, uuid } = queueItem;
      if (!data) {
        continue;
      }

      // Check if reset() has been called during decoding
      if (currentDecodeCounter !== this.decodeCounter) {
        this.isDecoding = false;
        break;
      }

      try {
        const { header, headerLength } = this.decodeHeader(data);
        const packetUuid = uuid || header.uuid || this.generateUUID();

        const streamData: StreamData = {
          audioStreamData: null,
          textStreamData: null,
          serverIngestTimeMs: header.ingest_time_ms || null,
          sinceLastMicro: header.since_last_micro || null,
          minStartTime: null,
          isEndOfSentence: header.has_eos ?? false,
          receiveTime: receiveTime || Date.now(), // Store receive time for delay calculation
        };

        if (isAudioStreamFormat(this.format)) {
          const audioData = data.slice(4 + headerLength);
          if (audioData.byteLength > 0) {
            const audioStreamData = await this.processAudio(
              audioData,
              receiveTime,
              packetUuid,
            );
            if (audioStreamData) {
              streamData.audioStreamData = audioStreamData;
            }
          }
        }

        this.onStreamData(streamData);
      } catch (error) {
        if (
          error &&
          typeof error === "object" &&
          "group" in error &&
          "code" in error
        ) {
          // Already an SDKError, pass it through
          this.onError(error as SDKError);
        } else {
          // Convert to unknown error with full stack trace
          const sdkError = createUnknownError(error, {
            context: "live_audio_decode",
            method: "live-audio-decoder.processQueue",
            streamFormat: this.format,
            timestamp: receiveTime || Date.now(),
          });
          this.onError(sdkError);
        }
      }
    }

    this.isDecoding = false;
  }

  private decodeHeader(data: ArrayBuffer): {
    header: StreamDataHeader;
    headerLength: number;
  } {
    try {
      const view = new DataView(data);
      const headerLength = view.getUint32(0);
      const slice = data.slice(4, headerLength + 4);
      const header = JSON.parse(this.textDecoder.decode(slice));
      return { header, headerLength };
    } catch (error) {
      const sdkError = withErrorData(AudioDecoderError.HeaderDecodingFailed, {
        reason:
          error instanceof Error
            ? error.message
            : "Failed to parse header JSON",
      });
      throw sdkError;
    }
  }

  private async processAudio(
    audioData: ArrayBuffer,
    receiveTime?: number,
    uuid?: string,
  ): Promise<AudioData | null> {
    try {
      if (!this.isDecoderInitialized) {
        await this.initializeDecoder();
      }

      if (!this.opusDecoder) {
        throw AudioDecoderError.DecoderNotInitialized;
      }

      let bytePos = 0;
      let opusFrames: Uint8Array[] = [];

      while (bytePos < audioData.byteLength) {
        const headerLen = 4;
        const header = audioData.slice(bytePos, bytePos + headerLen);

        if (header.byteLength < headerLen) {
          throw AudioDecoderError.InvalidOpusFrameHeader;
        }

        const headerView = new DataView(header);
        const nextFrameLen = headerView.getUint32(0);
        const nextFrameEnd = bytePos + headerLen + nextFrameLen;

        if (nextFrameEnd > audioData.byteLength) {
          throw AudioDecoderError.InvalidOpusFrameLength;
        }

        const opusFrame = audioData.slice(bytePos + headerLen, nextFrameEnd);
        opusFrames.push(new Uint8Array(opusFrame));

        bytePos = nextFrameEnd;
      }

      // Enhanced packet loss simulation
      const shouldSimulateLoss = this.shouldSimulatePacketLoss();
      if (shouldSimulateLoss) {
        console.log(
          `🔴 Simulating packet loss - frames: ${opusFrames.length}, consecutive losses: ${this.consecutiveLossCount + 1}, total packets: ${this.totalPacketCount}`,
        );
        opusFrames = []; // Simulate complete packet loss to trigger PLC
        this.consecutiveLossCount++;
      } else {
        if (this.consecutiveLossCount > 0) {
          console.log(
            `🟢 Packet recovered after ${this.consecutiveLossCount} consecutive losses`,
          );
          this.consecutiveLossCount = 0;
        }
      }

      // decodeFrames is async with the web worker
      const decodedAudio = await this.opusDecoder.decodeFrames(opusFrames);

      const samplesDecoded = decodedAudio.samplesDecoded;
      const channelData = decodedAudio.channelData;

      this.throughputTracker.receive(samplesDecoded);

      const streamData: AudioData = {
        timestamp: receiveTime || Date.now(),
        channelData: channelData,
        sampleRate: decodedAudio.sampleRate,
        frameLength: samplesDecoded,
        playbackRate: 1.0, // Live audio uses fixed rate
        uuid: uuid || "",
      };

      opusFrames = [];

      return streamData;
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "group" in error &&
        "code" in error
      ) {
        // Already an SDKError, pass it through
        this.onError(error as SDKError);
      } else {
        // Convert to unknown error with full stack trace
        const sdkError = createUnknownError(error, {
          context: "processAudio",
          method: "live-audio-decoder.processAudio",
          streamFormat: this.format,
        });
        this.onError(sdkError);
      }
      return null;
    }
  }

  private shouldSimulatePacketLoss(): boolean {
    this.totalPacketCount++;

    if (!this.packetLossConfig?.enabled) {
      return false;
    }

    const { lossRate, burstLength } = this.packetLossConfig;

    // If we're already in a loss burst, continue until burst is complete
    if (
      this.consecutiveLossCount > 0 &&
      this.consecutiveLossCount < burstLength
    ) {
      return true;
    }

    // If we've completed a burst, don't start a new one immediately
    if (this.consecutiveLossCount >= burstLength) {
      this.consecutiveLossCount = 0;
      return false;
    }

    // Random packet loss based on loss rate
    return Math.random() < lossRate;
  }

  private generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }
}
